import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './App.css';
import { NavBar, NavSection } from './components/nav-bar/nav-bar';
import { Greeting } from './components/greetings/greetings';
import { ProfessionalContact, ProfessionalPresence } from './components/professional-presence/professional-presence';
import { CareerCloudAiStack } from './components/career-history/career-cloud-ai-stack';
import { Footer } from './components/footer/footer';

function App() {
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('down');
  const [activeSection, setActiveSection] = useState<NavSection>('profile');

  useEffect(() => {
    document.title = 'Ahmed Magdy | Copiloteer & Cloud AI Solution Engineer';
  }, []);

  useEffect(() => {
    const career = document.getElementById('career');
    const sections = (['profile', 'work', 'career'] as NavSection[])
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => section !== null);

    if (!career || sections.length === 0) {
      return;
    }

    const updateScrollState = () => {
      const careerTop = window.scrollY + career.getBoundingClientRect().top;

      if (careerTop > 0) {
        setScrollDirection(window.scrollY < careerTop - 120 ? 'down' : 'up');
      }

      if (window.scrollY <= 1) {
        setActiveSection('profile');
        return;
      }

      const headerOffset = 120;
      const visibleSection = sections.reduce((active, section) => (
        section.getBoundingClientRect().top <= headerOffset ? section : active
      ), sections[0]);

      setActiveSection(visibleSection.id as NavSection);
    };

    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  const handleScrollCue = () => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    if (scrollDirection === 'up') {
      window.scrollTo({ top: 0, behavior });
      setScrollDirection('down');
      return;
    }

    const career = document.getElementById('career');

    if (!career) {
      return;
    }

    window.scrollTo({
      top: window.scrollY + career.getBoundingClientRect().top,
      behavior,
    });
    setScrollDirection('up');
  };

  const scrollCueLabel = scrollDirection === 'down'
    ? 'Scroll to career history'
    : 'Back to top';

  return (
    <div className="App">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <NavBar activeSection={activeSection} />
      <main id="main-content" tabIndex={-1}>
        <Greeting />
        <ProfessionalPresence />
        <CareerCloudAiStack />
        <ProfessionalContact />
      </main>
      <Footer />
      <button
        className={`scroll-cue scroll-cue--${scrollDirection}`}
        type="button"
        aria-label={scrollCueLabel}
        onClick={handleScrollCue}
      >
        {scrollDirection === 'down'
          ? <FaChevronDown aria-hidden="true" />
          : <FaChevronUp aria-hidden="true" />}
      </button>
    </div>
  );
}

export default App;
